/** @module csi/heading/_EnumMixin */
define([
	"dojo/_base/declare",
	"lodash",
	"src/c8-platform/service/aggregateStoreService",
	"csi/config",
	"csi/locale",
	"csi/reflection",
	"csi/store/_base",
	"csi/store/Names",
	"csi/xml"
], function(declare, _, {getCachedAggregateStoreDragFillId}, config, locale, reflection, csiStore, Names, xml) {

	return declare(null, {
		/**
		 * @override
		 * Use aggregate store drag-fill id to determine compatibility between nodes
		 */
		canCopy(ctxCopy, h) {
			return this.inherited(arguments) && (!this.hasAggregateStore(ctxCopy, h) || !!getCachedAggregateStoreDragFillId(ctxCopy.getNodeURL(), h.m_id));
		},

		/**
		 * @override
		 * Use aggregate store drag-fill id to determine if value being copied is available for paste node
		 */
		canPaste(ctxCopy, ctxPaste, h) {
			const key1 = getCachedAggregateStoreDragFillId(ctxCopy.getNodeURL(), h.m_id);
			const key2 = getCachedAggregateStoreDragFillId(ctxPaste.getNodeURL(), h.m_id);
			return this.inherited(arguments) && (key1 == key2);
		},

		getDependsOnValue: function(/*Context*/ctx, /*Attribute*/rca) {
			// summary: return value of enum attribute which heading depends on
			if (!rca)
				return null;

			var dependsInfo = rca.getDependsOnInfo();
			if (!dependsInfo)
				return null;

			var rcaDepends = dependsInfo.attr;

			var value = null;
			if (!dependsInfo.path) {
				value = ctx.getNodeAttributeValue(rcaDepends.getId());
			} else {
				// depending on the attribute type, we need to fetch
				// either the node this attribute belongs to
				// or the node this attribute refers to
				var isRef = xml.attributeTypeIsRef(rcaDepends.getType());
				var pathToNode = dependsInfo.path.getStandardPath();
				var node = ctx.cache.getPathNode(ctx.getNode(), pathToNode, true);
				if (node) {
					if (isRef) {
						var prefix = node.$Type;
						var subtypeName;
						if (node.Subtype) {
							subtypeName = config.getSubtypeName(node.Subtype);
						} else {
							subtypeName = xml.isValidURL(node.ProductType) ? ctx.cache.getNode(node.ProductType, true).$Name : "";
						}
						value = xml.enumCreate(prefix + "Subtype", subtypeName);
					} else {
						value = node[rcaDepends.getId()];
					}
				}
			}
			return value;
		},

		/**
		 * Return store for enum attribute
		 */
		getStore(ctx, h, value) {
			let store;

			if (h.m_id) {
				// use aggregate store
				const aggregateStoreParams = this._getAggregateStoreParams(ctx, h);
				const request = csiStore.getAggregateStoreRequest(aggregateStoreParams);

				// NOTE: although aggregate store returns currentValues, we still need to initialize the store with
				// the current value so that the widget can be created and considered valid before the query to the server
				// to get the currentValues and available values.
				const enumId = this._getFormat(ctx, h);
				const emptyValue = xml.enumCreate(enumId);
				const values = _.uniq([emptyValue].concat(value || []));
				const data = values.map((v) => csiStore.nameObject(locale.getEnumName(v), v));

				store = new Names({
					aggregateStoreParams,
					data,
					emptyValue,
					enumId,
					loc: ctx.locale,
					request,
				});
			}

			return store;
		},

		/**
		 * @override
		 * Return true as all enums have aggregate stores
		 */
		hasAggregateStore(ctx, h) {
			return true;
		},

		/**
		 * Check if dependent enum attribute, and if so, if the depends-on attribute is non-empty
		 */
		enumHasValidDependsOn(ctx, h) {
			const {m_id, _rca} = h;

			if (!_rca?.getDependsOnInfo()) {
				return true;
			}

			const values = ctx.getNodeAttributeValue(m_id);
			const valuesIsArray = Array.isArray(values);
			const dependsOnValue = this.getDependsOnValue(ctx, _rca);

			// allow editing if existing value is not empty
			if (valuesIsArray && values.length) {
				return true;
			} else if (!valuesIsArray && xml.valueIsValid(values, xml.ATTR_ENUM)) {
				return true;
			}

			// cannot edit dependent enum if depends on value is empty (rare case if trouble with resetting dependent value)
			if (!xml.valueIsValid(dependsOnValue, xml.ATTR_ENUM)) {
				return false;
			}

			// check if there are any valid dependent values for the selected value
			const enumText = xml.enumGetText(dependsOnValue);
			const enumList = reflection.getEnumList(h.m_format);
			return !!enumList?.values.some((enumValue) => enumValue.active && enumValue.isValidDependsOnValue(enumText));
		},
	});
});
